import React, { useState, useEffect, useCallback, useRef } from 'react';
import { FaArrowRightLong } from 'react-icons/fa6';
import { useLocation, useNavigate } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import { authentication as teamsAuthentication } from '@microsoft/teams-js';

import { API_URL } from '../../services/api';

// Toggle this to true to enable Azure AD SSO / login features
const ENABLE_AZURE_AD = false;

// KEEP THE REST OF YOUR IMPORTS AND THE COMPONENT STRUCTURE THE SAME
const Login = ({ onLogin, isTeamsInitialized }) => {
  // Added isTeamsInitialized prop
  const location = useLocation();
  const navigate = useNavigate();

  // NEW state to manage the two-step login flow
  const [loginStep, setLoginStep] = useState('identifier'); // 'identifier' -> 'password'

  // This state now manages the sub-flow *within* the 'password' step
  const [loginFlowState, setLoginFlowState] = useState('password'); // 'password' or 'otp_request' or 'otp_verify'

  const [isNewUserFlow, setIsNewUserFlow] = useState(false); // ### ADDED: State to track if it's a new user OTP flow

  const [formData, setFormData] = useState({
    identifier: '',
    password: '',
    otp: '',
    emailForOtpDisplay: '',
    actualEmailForVerification: '',
  });
  const [isLoading, setIsLoading] = useState(false);
  const [otpTimer, setOtpTimer] = useState(0);
  const [resendDisabled, setResendDisabled] = useState(false);

  // Enterprise / Azure AD login state
  const [showEnterpriseLogin, setShowEnterpriseLogin] = useState(false);
  // tenant_id read from URL (?tenant_id=xxx) — set by enterprise admins on their custom login link.
  // Not exposed as a manual input; Microsoft auto-handles work vs personal accounts.
  const enterpriseTenantId =
    new URLSearchParams(location.search).get('tenant_id') || '';

  // Silent SSO state — true while the background iframe auth check is running
  const [ssoChecking, setSsoChecking] = useState(ENABLE_AZURE_AD);
  const silentIframeRef = useRef(null);

  const oauthPopupRef = useRef(null);
  const popupPollerRef = useRef(null);

  // --- Silent Azure AD SSO on page load ---
  // fires for all users — enterprise session check is a lightweight background request; UX gain (zero-click login) outweighs the cost
  useEffect(() => {
    if (!ENABLE_AZURE_AD) {
      return;
    }
    let timeoutId;
    let messageHandler;

    const attemptSilentSSO = async () => {
      try {
        const tenantParam = enterpriseTenantId
          ? `?tenant_id=${encodeURIComponent(enterpriseTenantId)}`
          : '';
        const res = await fetch(
          `${API_URL}/api/auth/login/sso/redirect-url/microsoft/silent${tenantParam}`,
        );
        if (!res.ok) throw new Error(`Server error: ${res.status}`);
        const data = await res.json();
        if (!data.authUrl) throw new Error('No authUrl');

        // Derive expected origin — guard against relative API_URL (same-origin deployments)
        let backendOrigin;
        try {
          backendOrigin = new URL(API_URL).origin;
        } catch (_urlErr) {
          backendOrigin = window.location.origin;
        }

        // Create hidden iframe pointing to Microsoft silent auth
        const iframe = document.createElement('iframe');
        iframe.style.display = 'none';
        iframe.src = data.authUrl;
        document.body.appendChild(iframe);
        silentIframeRef.current = iframe;

        // Give up after 12 seconds (Microsoft is usually <2s for silent auth)
        timeoutId = setTimeout(() => {
          cleanup();
          setSsoChecking(false);
        }, 12000);

        messageHandler = (event) => {
          // Security: only trust messages from our own backend origin
          if (event.origin !== backendOrigin) return;
          if (!event.data?._silent_sso) return;
          cleanup();
          setSsoChecking(false);
          if (event.data.success && event.data.token) {
            onLogin(
              event.data.token,
              event.data.user_id,
              event.data.project_token,
            );
          }
          // If not success: fall through to normal login UI
        };
        window.addEventListener('message', messageHandler);
      } catch (_) {
        // Network error or no authUrl — just show normal login
        setSsoChecking(false);
      }
    };

    const cleanup = () => {
      if (timeoutId) clearTimeout(timeoutId);
      if (messageHandler) window.removeEventListener('message', messageHandler);
      if (silentIframeRef.current) {
        document.body.removeChild(silentIframeRef.current);
        silentIframeRef.current = null;
      }
    };

    attemptSilentSSO();
    return cleanup;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // --- START: Existing useEffect for handling URL error parameter ---
  useEffect(() => {
    const queryParams = new URLSearchParams(location.search);
    const errorParam = queryParams.get('error');

    if (errorParam) {
      const decodedError = decodeURIComponent(errorParam);
      toast.error(decodedError);
      const newSearchParams = new URLSearchParams(location.search);
      newSearchParams.delete('error');
      navigate({ search: newSearchParams.toString() }, { replace: true });
    }
  }, [location, navigate]);
  // --- END: Existing useEffect ---

  // --- START: Existing Callback for handling OAuth messages (for web flow) ---
  const handleOauthMessage = useCallback(
    (event) => {
      let expectedOrigin = '';
      try {
        expectedOrigin = new URL(API_URL).origin;
      } catch (e) {
        expectedOrigin = window.location.origin;
      }

      // Security: reject messages not from our backend origin
      if (event.origin !== expectedOrigin) return;

      if (event.data && typeof event.data === 'object') {
        if (event.data._silent_sso) return; // handled by silent SSO effect
        const { success, token, user_id, project_token, error, provider } =
          event.data;
        if (typeof success === 'undefined' && !token && !error) {
          // More specific check
          return;
        }
        if (oauthPopupRef.current && !oauthPopupRef.current.closed) {
          oauthPopupRef.current.close();
        }
        if (popupPollerRef.current) {
          clearInterval(popupPollerRef.current);
          popupPollerRef.current = null;
        }
        oauthPopupRef.current = null;
        setIsLoading(false);

        if (success) {
          toast.success(
            `Logged in successfully with ${provider || 'OAuth provider'}!`,
          );
          onLogin(token, user_id, project_token); // Assuming onLogin expects (token, userId)
        } else {
          toast.error(error || 'OAuth login failed. Please try again.');
        }
      }
    },
    // eslint-disable-next-line
    [onLogin, API_URL],
  );
  // --- END: Existing Callback for handling OAuth messages ---

  // --- START: Existing useEffect for setting up message listener (for web flow) ---
  useEffect(() => {
    // Only add this listener if not in Teams context or as a fallback
    // For this setup, we'll have initiateOAuthLogin decide based on context
    // So, this listener is primarily for the non-Teams web flow.
    window.addEventListener('message', handleOauthMessage);
    return () => {
      window.removeEventListener('message', handleOauthMessage);
      if (popupPollerRef.current) {
        clearInterval(popupPollerRef.current);
      }
      if (oauthPopupRef.current && !oauthPopupRef.current.closed) {
        oauthPopupRef.current.close();
      }
    };
  }, [handleOauthMessage]);
  // --- END: Existing useEffect ---

  // --- START: Existing openOAuthPopup (for web flow) ---
  const openOAuthPopup = (authUrl) => {
    const width = 600,
      height = 700;
    const left = window.screen.width / 2 - width / 2;
    const top = window.screen.height / 2 - height / 2;

    if (oauthPopupRef.current && !oauthPopupRef.current.closed) {
      oauthPopupRef.current.close();
    }
    if (popupPollerRef.current) {
      clearInterval(popupPollerRef.current);
    }

    oauthPopupRef.current = window.open(
      authUrl,
      'oauthWindow',
      `toolbar=no,location=no,directories=no,status=no,menubar=no,
       scrollbars=yes,resizable=yes,copyhistory=no,width=${width},
       height=${height},top=${top},left=${left}`,
    );

    if (oauthPopupRef.current) {
      setIsLoading(true);
      popupPollerRef.current = setInterval(() => {
        if (oauthPopupRef.current && oauthPopupRef.current.closed) {
          clearInterval(popupPollerRef.current);
          popupPollerRef.current = null;
          if (isLoading) {
            toast.error(
              'Login process cancelled or popup closed unexpectedly.',
            );
            setIsLoading(false);
          }
          oauthPopupRef.current = null;
        } else if (!oauthPopupRef.current) {
          clearInterval(popupPollerRef.current);
          popupPollerRef.current = null;
        }
      }, 500);
    } else {
      toast.error(
        'Failed to open login window. Please check your popup blocker settings.',
      );
      setIsLoading(false);
    }
  };
  // --- END: Existing openOAuthPopup ---

  // --- START: REVISED initiateWebOAuthLogin (WEB FLOW ONLY) ---
  const initiateWebOAuthLogin = (providerBackendEndpointName) => {
    // 1. Open the popup window IMMEDIATELY to our new intermediate page.
    // This is a direct result of the user's click, so it won't be blocked.
    setIsLoading(true);

    try {
      // 2. Construct the URL for our intermediate page, passing the necessary
      // info (provider endpoint and API URL) as query parameters.
      const authStartPageUrl =
        `${window.location.origin}/web-auth-start.html` +
        `?provider_backend_endpoint_name=${providerBackendEndpointName}` +
        `&api_uri=${encodeURIComponent(API_URL)}`;

      // 3. Use the existing openOAuthPopup function. It already handles
      // popup window references, polling for closure, and other edge cases.
      openOAuthPopup(authStartPageUrl);
    } catch (err) {
      toast.error(
        err.message ||
          `Could not initiate Sign-In with ${providerBackendEndpointName}.`,
      );
      setIsLoading(false);
    }
  };

  // --- START: NEW initiateTeamsOAuthLogin (TEAMS FLOW ONLY) ---
  const initiateTeamsOAuthLogin = async (
    providerBackendEndpointName,
    providerShortName,
  ) => {
    if (!isTeamsInitialized) {
      toast.error('Teams environment not ready. Please try again.');
      setIsLoading(false);
      return;
    }
    setIsLoading(true);
    try {
      const finalSuccessRedirectInApp = window.location.origin + '/projects';
      const backendCallbackUri = `${API_URL}/oauth2callback`; // Ensure API_URL is defined
      const teamsStateObject = {
        original_frontend_url: finalSuccessRedirectInApp,
        app_name: providerShortName,
        isTeamsAuth: true,
      };
      const teamsCustomState = btoa(JSON.stringify(teamsStateObject));
      const authStartPageUrl =
        `${window.location.origin}/teams-auth-start.html` +
        `?provider_backend_endpoint_name=${providerBackendEndpointName}` +
        `&teams_custom_state=${encodeURIComponent(teamsCustomState)}` +
        `&backend_callback_uri=${encodeURIComponent(backendCallbackUri)}` +
        `&api_uri=${encodeURIComponent(API_URL)}`;

      // Use the Promise-based version of authenticate
      const result = await teamsAuthentication.authenticate({
        url: authStartPageUrl,
        width: 600,
        height: 700,
        isExternal: false, // Recommended for most tab scenarios
      });

      // Success: 'result' contains the string passed to notifySuccess()
      setIsLoading(false);
      if (result) {
        try {
          const authData = JSON.parse(result); // Expecting { token, user_id, provider }
          toast.success(
            `Logged in successfully with ${authData.provider || 'OAuth provider'} via Teams!`,
          );
          onLogin(authData.token, authData.user_id, authData.project_token);
        } catch (e) {
          toast.error('Failed to process login data from Teams.');
        }
      } else {
        // This case should be less common with the promise, as an empty result might still be a "success"
        // but an error during the flow would throw and be caught by the catch block.
        toast.error(
          'OAuth login via Teams completed, but no result data was returned.',
        );
      }
    } catch (error) {
      // This will catch errors from authenticate() or other issues
      setIsLoading(false);
      // The 'error' here could be a string (reason from notifyFailure) or an Error object
      const errorMessage =
        error instanceof Error ? error.message : String(error);
      toast.error(`Teams OAuth failed: ${errorMessage || 'Unknown error'}`);
    }
  };

  // --- END: NEW initiateTeamsOAuthLogin ---
  // --- CORRECTED: OAuth button handlers ---
  const handleGoogleLogin = () => {
    if (isTeamsInitialized) {
      // Only rely on isTeamsInitialized
      initiateTeamsOAuthLogin('redirect-url/google', 'google');
    } else {
      initiateWebOAuthLogin('redirect-url/google');
    }
  };

  const handleMicrosoftLogin = () => {
    if (isTeamsInitialized) {
      // Only rely on isTeamsInitialized
      initiateTeamsOAuthLogin('redirect-url/outlook', 'outlook');
    } else {
      initiateWebOAuthLogin('redirect-url/outlook');
    }
  };

  const handleEnterpriseLogin = () => {
    const tenantId = enterpriseTenantId.trim();
    const endpoint = tenantId
      ? `sso/redirect-url/microsoft?tenant_id=${encodeURIComponent(tenantId)}`
      : 'sso/redirect-url/microsoft';
    if (isTeamsInitialized) {
      initiateTeamsOAuthLogin(endpoint, 'microsoft_sso');
    } else {
      initiateWebOAuthLogin(endpoint);
    }
  };

  const handleLinkedInLogin = () => {
    if (isTeamsInitialized) {
      // Only rely on isTeamsInitialized
      initiateTeamsOAuthLogin('redirect-url/linkedin', 'linkedin');
    } else {
      initiateWebOAuthLogin('redirect-url/linkedin');
    }
  };
  // --- END: Modified OAuth button handlers ---

  // KEEP ALL YOUR OTHER HANDLERS (handleChange, password sign-in, OTP, etc.) AND JSX THE SAME
  // ... (formData, handleChange, startResendOtpTimer, useEffect for timer, handlePasswordSignIn, handleRequestLoginOtp, handleOtpSignIn, switchToOtpFlow, switchToPasswordFlow)
  // ... (currentFormSubmitHandler, mainButtonText logic)
  // ... (return statement with JSX)

  // Ensure the prop `isTeamsInitialized` is passed from App.js
  // Example of how it might be logged (optional):

  // Your existing password and OTP logic:
  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const goBackToIdentifierStep = () => {
    setLoginStep('identifier');
    setLoginFlowState('password'); // Reset sub-flow to default
    setFormData((prev) => ({ ...prev, password: '', otp: '' }));
  };

  // --- NEW: Step 1 Handler ---
  const handleIdentifierCheck = async (e) => {
    e.preventDefault();
    const { identifier } = formData;
    if (!identifier) {
      toast.error('Please provide your Email.');
      return;
    }
    setIsLoading(true);
    try {
      // This endpoint needs to be created on your backend as discussed.
      const response = await fetch(`${API_URL}/api/auth/check-identifier`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ identifier }),
      });
      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.detail || 'Error checking your details.');
      }

      if (data.user_exists) {
        // User exists, so proceed to the password step
        setLoginStep('password');
      } else {
        if (data.is_email) {
          // User does not exist, but it's an email -> redirect to signup
          navigate('/sign-up', {
            state: {
              email: data.email,
              suggested_username: data.suggested_username,
            },
          });
        } else {
          // User does not exist and it's not an email -> show error
          toast.error('Invalid Email.');
        }
      }
    } catch (err) {
      toast.error(err.message || 'An unexpected error occurred.');
    } finally {
      setIsLoading(false);
    }
  };

  const startResendOtpTimer = () => {
    setResendDisabled(true);
    setOtpTimer(60);
  };

  useEffect(() => {
    let intervalId = null;
    if (resendDisabled && otpTimer > 0) {
      intervalId = setInterval(() => {
        setOtpTimer((prevTimer) => {
          if (prevTimer <= 1) {
            clearInterval(intervalId);
            setResendDisabled(false);
            return 0;
          }
          return prevTimer - 1;
        });
      }, 1000);
    }
    return () => {
      if (intervalId) {
        clearInterval(intervalId);
      }
    };
  }, [resendDisabled, otpTimer]);

  const handlePasswordSignIn = async (e) => {
    e.preventDefault();
    const { identifier, password } = formData;
    if (!identifier || !password) {
      toast.error('Please fill in your Email and Password.');
      return;
    }
    setIsLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/auth/login`, {
        // Assuming /api/auth/login is correct
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ identifier, password }),
        credentials: 'include',
      });
      const data = await response.json();
      if (!response.ok) {
        const errorMsg = data.detail || data.message || 'Login failed.';
        throw new Error(errorMsg); // Throw an error object
      }
      toast.success(data.message || 'Logged in Successfully!');
      // Assuming your onLogin expects (token, user_id) but backend sends {token, user_id, message}
      onLogin(data.token, data.user_id, data.project_token);
    } catch (err) {
      toast.error(
        err.message || 'Login failed. Please check your credentials.',
      );
    } finally {
      setIsLoading(false);
    }
  };

  const handleRequestLoginOtp = async (e) => {
    if (e) e.preventDefault();
    const { identifier } = formData;
    if (!identifier) {
      toast.error('Please enter your Email to receive a code.');
      return;
    }
    setIsLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/auth/login/send-otp`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ identifier }),
        credentials: 'include',
      });
      const data = await response.json();
      if (!response.ok) {
        const errorMsg =
          data.detail || data.message || 'Failed to send one-time code.';
        throw new Error(errorMsg);
      }
      //  ### ADDED: This is the key logic to determine the next step
      // The backend sends `user_exists: true` or `user_exists: false`.
      // We set our new state based on this. `isNewUserFlow` will be true if user_exists is false.
      setIsNewUserFlow(data.new_user || false);

      toast.success(
        data.message || 'One-time code sent to your registered email.',
      );
      setFormData((prev) => ({
        ...prev,
        emailForOtpDisplay: data.email_sent_to,
        actualEmailForVerification: data.actual_email_for_verification,
        otp: '',
      }));
      setLoginFlowState('otp_verify');
      startResendOtpTimer();
    } catch (err) {
      toast.error(
        err.message || 'Could not send one-time code. Please try again.',
      );
    } finally {
      setIsLoading(false);
    }
  };

  const handleOtpSignIn = async (e) => {
    e.preventDefault();
    const { actualEmailForVerification, otp } = formData;
    if (!otp) {
      toast.error('Please enter the one-time code.');
      return;
    }
    if (!actualEmailForVerification) {
      toast.error(
        'Verification email not found. Please try requesting a code again.',
      );
      return;
    }
    setIsLoading(true);

    // ### ADDED: Conditional logic for endpoint and payload
    const endpoint = isNewUserFlow
      ? `${API_URL}/api/auth/login/verify-and-create-user` // Endpoint for new users
      : `${API_URL}/api/auth/login/verify-otp`; // Endpoint for existing users
    try {
      const response = await fetch(endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email_for_verification: actualEmailForVerification,
          otp,
        }),
        credentials: 'include',
      });
      const data = await response.json();
      if (!response.ok) {
        const errorMsg =
          data.detail || data.message || 'Sign in with code failed.';
        throw new Error(errorMsg);
      }
      toast.success(data.message || 'Logged in Successfully with code!');
      onLogin(data.token, data.user_id, data.project_token);
    } catch (err) {
      toast.error(
        err.message ||
          'Sign in with code failed. Please check the code or try again.',
      );
    } finally {
      setIsLoading(false);
    }
  };

  const switchToOtpFlow = () => {
    setLoginFlowState('otp_request');
    // ### ADDED: Reset the flow type when switching
    setIsNewUserFlow(false);
    setFormData((prev) => ({
      ...prev,
      password: '',
      otp: '',
    }));
  };

  const switchToPasswordFlow = () => {
    setLoginFlowState('password');
    // ### ADDED: Reset the flow type when switching back
    setIsNewUserFlow(false);
    setFormData((prev) => ({
      ...prev,
      otp: '',
    }));
  };

  let currentFormSubmitHandler;
  let mainButtonText;

  if (loginStep === 'identifier') {
    currentFormSubmitHandler = handleIdentifierCheck;
    mainButtonText = 'Continue';
  } else {
    // loginStep === 'password'
    if (loginFlowState === 'password') {
      currentFormSubmitHandler = handlePasswordSignIn;
      mainButtonText = 'Sign in';
    } else if (loginFlowState === 'otp_request') {
      currentFormSubmitHandler = handleRequestLoginOtp;
      mainButtonText = 'Send Code';
    } else {
      // otp_verify
      currentFormSubmitHandler = handleOtpSignIn;
      mainButtonText = 'Sign In with Code';
    }
  }

  const theme = 'light'; // Later replace with context or store
  const isDark = theme === 'dark';

  return (
    <div
      className={`
      relative flex h-screen w-screen flex-col overflow-hidden md:flex-row
      ${isDark ? 'bg-black' : 'bg-radial-gradient'}
    `}
    >
      {/* SILENT SSO — small top-right indicator, login page stays fully interactive */}
      {ENABLE_AZURE_AD && ssoChecking && (
        <div className="absolute right-4 top-4 z-50 flex items-center gap-2 rounded-full border border-zinc-200/60 bg-white/90 px-3 py-1.5 shadow-sm backdrop-blur-sm dark:border-zinc-700/60 dark:bg-zinc-900/90">
          <svg
            className="h-3.5 w-3.5 animate-spin text-[#6a65ef]"
            viewBox="0 0 24 24"
            fill="none"
          >
            <circle
              className="opacity-25"
              cx="12"
              cy="12"
              r="10"
              stroke="currentColor"
              strokeWidth="4"
            />
            <path
              className="opacity-75"
              fill="currentColor"
              d="M4 12a8 8 0 018-8v8z"
            />
          </svg>
          <span className="text-xs text-zinc-500 dark:text-zinc-400">
            Checking enterprise session…
          </span>
        </div>
      )}
      {/* DARK BG IMAGE */}
      {isDark && (
        <img
          src="../assets/auth-bg.svg"
          className="absolute inset-0 z-0 hidden h-full w-full object-cover lg:block"
          alt="Background"
        />
      )}

      {/* LEFT LOGIN */}
      <div className="relative z-10 flex w-full flex-col items-center justify-center overflow-y-auto p-5 lg:w-1/2">
        <div className="w-full max-w-md">
          {/* LOGO */}
          <div className="mb-5 flex items-center">
            <img
              alt="SuperManager AGI"
              src="../assets/super_agi_logo.png"
              className="h-16 w-16"
            />
            <h1
              className={`${isDark ? 'text-white' : 'text-black'} font-poppins ml-2 text-lg font-bold`}
            >
              SuperManager AGI
            </h1>
          </div>

          <h2
            className={`${isDark ? 'text-white' : 'text-black'} mb-6 text-2xl font-bold`}
          >
            Sign In
          </h2>

          <form onSubmit={currentFormSubmitHandler}>
            {/* IDENTIFIER */}
            <div className="relative mb-4">
              <label
                htmlFor="email"
                className={`text-sm ${isDark ? 'text-zinc-400' : 'text-zinc-600'}`}
              >
                Email
              </label>

              <input
                name="identifier"
                value={formData.identifier}
                onChange={handleChange}
                disabled={isLoading || loginStep === 'password'}
                placeholder="Enter your email"
                className={`
                w-full rounded-lg border-l-2 px-3 py-3 pr-10 text-sm focus:outline-none
                ${
                  isDark
                    ? 'border-transparent bg-[#1E1F20] text-zinc-200 focus:border-[#6a65ef]'
                    : 'border-zinc-300 bg-white text-zinc-900 focus:border-[#6a65ef]'
                }
              `}
              />

              {loginStep === 'password' && (
                <button
                  type="button"
                  onClick={goBackToIdentifierStep}
                  className="absolute right-3 top-9 text-zinc-400 hover:text-white"
                >
                  ✎
                </button>
              )}
            </div>

            {/* PASSWORD + OTP SECTION */}
            {loginStep === 'password' && (
              <>
                {/* PASSWORD */}
                {loginFlowState === 'password' && (
                  <div className="mb-4">
                    <label
                      htmlFor="password"
                      className={`text-sm ${isDark ? 'text-zinc-400' : 'text-zinc-600'}`}
                    >
                      Password
                    </label>

                    <input
                      name="password"
                      type="password"
                      value={formData.password}
                      onChange={handleChange}
                      placeholder="Enter your password"
                      className={`
                      w-full rounded-lg border-l-2 px-3 py-3 text-sm focus:outline-none
                      ${
                        isDark
                          ? 'border-transparent bg-[#1E1F20] text-zinc-200 focus:border-[#6a65ef]'
                          : 'border-zinc-300 bg-white text-zinc-900 focus:border-[#6a65ef]'
                      }
                    `}
                    />
                  </div>
                )}

                {/* OTP VERIFY */}
                {loginFlowState === 'otp_verify' && (
                  <div className="mb-4">
                    <label
                      className={`text-sm ${isDark ? 'text-zinc-400' : 'text-zinc-600'}`}
                    >
                      One-time code sent to{' '}
                      {formData.emailForOtpDisplay || 'your email'}
                    </label>

                    <input
                      name="otp"
                      value={formData.otp}
                      onChange={handleChange}
                      maxLength={6}
                      placeholder="Enter your 6-Digit OTP"
                      className={`
                      w-full rounded-lg border-l-2 px-3 py-3 text-sm focus:outline-none
                      ${
                        isDark
                          ? 'border-transparent bg-[#1E1F20] text-zinc-200 focus:border-[#6a65ef]'
                          : 'border-zinc-300 bg-white text-zinc-900 focus:border-[#6a65ef]'
                      }
                    `}
                    />

                    {/* RESEND OTP */}
                    <button
                      type="button"
                      onClick={handleRequestLoginOtp}
                      disabled={resendDisabled || isLoading}
                      className={`mt-2 text-xs ${
                        resendDisabled
                          ? 'text-zinc-500'
                          : isDark
                            ? 'text-white underline'
                            : 'text-[#6a65ef] underline'
                      }`}
                    >
                      {resendDisabled
                        ? `Resend code in ${otpTimer}s`
                        : 'Resend code'}
                    </button>
                  </div>
                )}
              </>
            )}

            {/* MAIN BUTTON */}
            <div className="flex flex-col items-start gap-4">
              <button
                type="submit"
                disabled={isLoading}
                className="flex w-auto items-center gap-4 rounded-3xl border bg-white p-[1px]"
              >
                <div className="font-poppins flex h-9 items-center justify-center px-6 font-medium text-black">
                  {isLoading
                    ? loginFlowState === 'otp_request'
                      ? 'Sending...'
                      : loginFlowState === 'otp_verify'
                        ? 'Verifying...'
                        : 'Signing in...'
                    : mainButtonText}
                </div>

                {!isLoading && (
                  <span className="mr-[1px] flex h-9 w-9 items-center justify-center rounded-full bg-[#6a65ef] text-lg text-white">
                    <FaArrowRightLong />
                  </span>
                )}
              </button>

              {/* SWITCH PASSWORD / OTP */}
              {loginStep === 'password' && (
                <div className="flex items-center justify-center gap-6">
                  {loginFlowState === 'password' && (
                    <button
                      type="button"
                      onClick={switchToOtpFlow}
                      className="text-sm text-[#6a65ef] hover:underline"
                    >
                      Sign in with one-time code
                    </button>
                  )}

                  {loginFlowState !== 'password' && (
                    <button
                      type="button"
                      onClick={switchToPasswordFlow}
                      className="text-sm text-[#6a65ef] hover:underline"
                    >
                      Sign in with password
                    </button>
                  )}
                </div>
              )}
            </div>
          </form>

          {/* OR */}
          <div className="my-6 flex items-center">
            <hr className="w-full border-zinc-600" />
            <span className="px-2 text-sm text-zinc-400">OR</span>
            <hr className="w-full border-zinc-600" />
          </div>

          {/* OAUTH */}
          <div className="flex justify-center gap-6">
            <button
              onClick={handleGoogleLogin}
              className="h-11 w-11 rounded-full bg-white p-1"
            >
              <img alt="Google" src="../assets/google.svg" className="h-full" />
            </button>

            <button
              onClick={handleMicrosoftLogin}
              className="h-11 w-11 rounded-full bg-white p-1"
            >
              <img
                alt="Microsoft"
                src="../assets/icons8-microsoft-outlook.svg"
                className="h-full"
              />
            </button>

            <button
              onClick={handleLinkedInLogin}
              className="h-11 w-11 rounded-full"
              style={{ background: '#0288d1' }}
            >
              <img
                alt="LinkedIn"
                src="../assets/icons8-linkedin.svg"
                className="h-full"
              />
            </button>
          </div>

          {/* TERMS */}
          <p className="mt-8 flex justify-center gap-4 text-sm text-gray-400">
            <a
              href="https://www.supermanager.co/terms-of-service"
              className="underline"
            >
              Terms of Service
            </a>
            <a
              href="https://www.supermanager.co/privacy-policy"
              className="underline"
            >
              Privacy Policy
            </a>
          </p>

          {/* ENTERPRISE / AZURE AD SSO */}
          {ENABLE_AZURE_AD && (
            <div className="mt-6">
              <button
                type="button"
                onClick={() => setShowEnterpriseLogin((v) => !v)}
                className={`w-full text-sm ${
                  isDark
                    ? 'text-zinc-400 hover:text-white'
                    : 'text-zinc-500 hover:text-zinc-800'
                } flex items-center justify-center gap-2 transition-colors`}
              >
                <svg
                  className="h-4 w-4"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <rect x="2" y="7" width="20" height="14" rx="2" />
                  <path d="M16 7V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v2" />
                </svg>
                Enterprise / Azure AD login
                <svg
                  className={`h-3 w-3 transition-transform ${
                    showEnterpriseLogin ? 'rotate-180' : ''
                  }`}
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <path d="m6 9 6 6 6-6" />
                </svg>
              </button>

              {showEnterpriseLogin && (
                <div className="mt-3">
                  <button
                    type="button"
                    onClick={handleEnterpriseLogin}
                    disabled={isLoading}
                    className="flex w-full items-center justify-center gap-2 rounded-lg border border-[#6a65ef] px-4 py-2 text-sm font-medium text-[#6a65ef] transition-colors hover:bg-[#6a65ef] hover:text-white disabled:opacity-50"
                  >
                    <svg
                      className="h-4 w-4"
                      viewBox="0 0 23 23"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path fill="#f35325" d="M1 1h10v10H1z" />
                      <path fill="#81bc06" d="M12 1h10v10H12z" />
                      <path fill="#05a6f0" d="M1 12h10v10H1z" />
                      <path fill="#ffba08" d="M12 12h10v10H12z" />
                    </svg>
                    Continue with Microsoft
                    {enterpriseTenantId && (
                      <span className="rounded bg-[#6a65ef]/10 px-1 py-0.5 text-xs font-normal">
                        tenant locked
                      </span>
                    )}
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* RIGHT PANEL LIGHT ONLY */}
      {!isDark && (
        <div className="hidden w-1/2 items-center justify-center p-16 lg:flex">
          <div className="max-w-xl">
            <h1 className="mb-6 text-5xl font-semibold text-zinc-900">
              See autonomous project management running on your real projects.
            </h1>

            <p className="mb-8 text-lg text-zinc-600">
              Experience autonomous project management in action.
            </p>

            <p className="mb-8 text-sm text-zinc-500">
              We may request additional information to verify your identity.
            </p>

            <div className="flex gap-6 text-sm text-zinc-700">
              <span className="cursor-pointer underline">Documentation</span>
              <span className="cursor-pointer underline">FAQ</span>
              <span className="cursor-pointer underline">Resource Hub</span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Login;
